import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronDown, ChevronUp, Phone, User, Hash, FileText, Trash2, Save, Minus, Plus, AlertTriangle, XCircle, Scale } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { imgUrl, fmtGram, fmtDate, STATUS_COLORS } from "@/lib/format";
import GramSummary from "@/components/GramSummary";
import InvoicePanel from "@/components/InvoicePanel";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["Yeni Sipariş", "Sipariş Onaylandı", "İstanbul'a Geçildi", "Üretimde", "Hazırlanıyor", "Kargoya Verildi", "Tamamlandı", "İptal Edildi", "Reddedildi"];

export default function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [edits, setEdits] = useState({});
  const [savingItems, setSavingItems] = useState(null);
  const [gramEdits, setGramEdits] = useState({});
  const [savingGram, setSavingGram] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const r = await api.get("/admin/orders", { params });
      setOrders(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const updateOrder = (updated) => setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));

  const toggle = (o) => {
    if (expanded === o.id) { setExpanded(null); return; }
    setExpanded(o.id);
    setEdits((p) => ({ ...p, [o.id]: o.items.map((it) => ({ ...it })) }));
    setGramEdits((p) => ({ ...p, [o.id]: Object.fromEntries(o.items.map((it) => [it.product_id, Array.from({ length: it.quantity }, (_, i) => (it.actual_grams && it.actual_grams[i] != null) ? it.actual_grams[i] : "")])) }));
  };
  const editList = (id) => edits[id] || [];
  const setItemQty = (id, pid, qty) => setEdits((p) => ({ ...p, [id]: (p[id] || []).map((it) => it.product_id === pid ? { ...it, quantity: Math.max(1, qty) } : it) }));
  const removeItem = (id, pid) => setEdits((p) => ({ ...p, [id]: (p[id] || []).filter((it) => it.product_id !== pid) }));
  const resetItems = (o) => setEdits((p) => ({ ...p, [o.id]: o.items.map((it) => ({ ...it })) }));
  const isDirty = (o) => {
    const l = edits[o.id];
    if (!l) return false;
    if (l.length !== o.items.length) return true;
    return o.items.some((it) => { const e = l.find((x) => x.product_id === it.product_id); return !e || e.quantity !== it.quantity; });
  };
  const saveItems = async (o) => {
    const l = edits[o.id] || [];
    if (l.length === 0) { toast.error("Siparişte en az bir ürün kalmalı"); return; }
    setSavingItems(o.id);
    try {
      const { data } = await api.put(`/admin/orders/${o.id}/items`, { items: l.map((it) => ({ product_id: it.product_id, quantity: it.quantity })) });
      updateOrder(data);
      setEdits((p) => ({ ...p, [o.id]: data.items.map((it) => ({ ...it })) }));
      toast.success("Sipariş güncellendi, bakiye yeniden hesaplandı");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSavingItems(null); }
  };

  const saveDelivery = async (o, val) => {
    try {
      const { data } = await api.put(`/admin/orders/${o.id}/delivery`, { est_delivery: val });
      updateOrder(data);
      toast.success("Tahmini teslim güncellendi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const setGramVal = (id, pid, idx, val) => setGramEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), [pid]: ((p[id] || {})[pid] || []).map((g, i) => (i === idx ? val : g)) } }));
  const saveGrams = async (o) => {
    const g = gramEdits[o.id] || {};
    const items = Object.entries(g).map(([pid, arr]) => ({ product_id: pid, grams: arr.map((x) => parseFloat(x)).filter((x) => !isNaN(x)) }));
    setSavingGram(o.id);
    try {
      const { data } = await api.put(`/admin/orders/${o.id}/actual-grams`, { items });
      updateOrder(data);
      setGramEdits((p) => ({ ...p, [o.id]: Object.fromEntries(data.items.map((it) => [it.product_id, Array.from({ length: it.quantity }, (_, i) => (it.actual_grams && it.actual_grams[i] != null) ? it.actual_grams[i] : "")])) }));
      toast.success(data.gram_finalized ? "Gerçek gram kaydedildi, bakiye kesinleşti" : "Gramlar kaydedildi (eksik parçalar var)");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSavingGram(null); }
  };

  const changeStatus = async (order, status) => {
    try {
      const { data } = await api.put(`/admin/orders/${order.id}/status`, { status });
      updateOrder(data);
      toast.success(`Durum güncellendi: ${status}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const inputCls = "bg-surface border-border text-foreground focus:ring-gold h-9";

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-foreground">Siparişler</h1>
          <p className="text-sm text-muted-foreground mt-1">{orders.length} sipariş</p>
        </div>
        <div className="w-52">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="order-status-filter" className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface border-border text-foreground">
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>
      ) : orders.length === 0 ? (
        <p className="text-center text-muted-foreground py-16">Sipariş bulunamadı.</p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const isOpen = expanded === o.id;
            return (
              <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="bg-surface border border-border rounded-md overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-gold">{o.order_number}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded border ${STATUS_COLORS[o.status] || "border-border text-muted-foreground"}`}>{o.status}</span>
                        <span data-testid={`gram-badge-${o.order_number}`} className={`text-[11px] px-2 py-0.5 rounded border ${o.gram_finalized ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>{o.gram_finalized ? "Kesinleşen Gram" : "Tahmini Gram"}</span>
                        {o.over_limit && <span data-testid={`limit-badge-${o.order_number}`} className="text-[11px] px-2 py-0.5 rounded border border-amber-500/40 text-amber-400 bg-amber-500/10 flex items-center gap-1"><AlertTriangle size={12} /> Limit Aşımı</span>}
                      </div>
                      <div className="font-medium text-foreground mt-1">{o.company_name}</div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1"><User size={12} /> {o.contact_person}</span>
                        <span className="flex items-center gap-1"><Phone size={12} /> {o.phone}</span>
                        <span className="flex items-center gap-1"><Hash size={12} /> {o.customer_code}</span>
                        <span>{fmtDate(o.created_at)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gold">Has {fmtGram(o.total_has)} gr {!o.gram_finalized && <span className="text-[11px] text-amber-400">(tahmini)</span>}</div>
                      <div className="text-[11px] text-muted-foreground">saf {fmtGram(o.total_pure_has)} · işç {fmtGram(o.total_isc_has)} gr · {o.items.length} ürün</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-4 flex-wrap">
                    <div className="w-52">
                      <Select value={o.status} onValueChange={(v) => changeStatus(o, v)}>
                        <SelectTrigger data-testid={`order-status-select-${o.order_number}`} className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-surface border-border text-foreground">
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <input data-testid={`delivery-input-${o.order_number}`} defaultValue={o.est_delivery || ""} onBlur={(e) => { if (e.target.value !== (o.est_delivery || "")) saveDelivery(o, e.target.value); }} placeholder="Tahmini teslim (örn. 7–10 iş günü)" className={`${inputCls} w-56`} />
                    <button data-testid={`proforma-${o.order_number}`} onClick={() => navigate(`/invoice/${o.id}`)} className="flex items-center gap-1 text-sm text-gold hover:underline">
                      <FileText size={15} /> Proforma
                    </button>
                    {o.status !== "Reddedildi" && o.status !== "İptal Edildi" && (
                      <button data-testid={`reject-order-${o.order_number}`} onClick={() => changeStatus(o, "Reddedildi")} className="flex items-center gap-1 text-sm text-red-400 hover:underline">
                        <XCircle size={15} /> Reddet
                      </button>
                    )}
                    <button data-testid={`toggle-order-${o.order_number}`} onClick={() => toggle(o)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-gold transition-colors ml-auto">
                      Detay {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-border bg-obsidian/50 p-4 space-y-4">
                    <div className="space-y-3">
                      {editList(o.id).map((it) => {
                        const lineGram = (Number(it.gram) || 0) * it.quantity;
                        return (
                          <div key={it.product_id} data-testid={`admin-order-item-${it.code}`} className="flex gap-3 items-center">
                            <div className="w-12 h-14 rounded overflow-hidden bg-elevated shrink-0">
                              {it.image_url && <img src={imgUrl(it.image_url)} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-foreground">{it.name}</div>
                              <div className="text-[11px] text-muted-foreground font-mono">{it.code} · {it.karat} Ayar · İşçilik {it.milyem || 0} milyem</div>
                            </div>
                            <div className="flex items-center border border-border rounded-md">
                              <button data-testid={`item-dec-${it.code}`} onClick={() => setItemQty(o.id, it.product_id, it.quantity - 1)} className="p-1.5 hover:text-gold transition-colors"><Minus size={13} /></button>
                              <span className="w-7 text-center text-sm">{it.quantity}</span>
                              <button data-testid={`item-inc-${it.code}`} onClick={() => setItemQty(o.id, it.product_id, it.quantity + 1)} className="p-1.5 hover:text-gold transition-colors"><Plus size={13} /></button>
                            </div>
                            <div className="text-right text-sm w-20">
                              <div className="text-gold font-medium">{fmtGram(lineGram)} gr</div>
                            </div>
                            <button data-testid={`item-remove-${it.code}`} onClick={() => removeItem(o.id, it.product_id)} className="text-muted-foreground hover:text-red-400 transition-colors p-1" title="Siparişten çıkar"><Trash2 size={16} /></button>
                          </div>
                        );
                      })}
                    </div>

                    {isDirty(o) && (
                      <div className="flex items-center gap-2 justify-end">
                        <button data-testid={`reset-items-${o.order_number}`} onClick={() => resetItems(o)} className="px-3 h-9 border border-border rounded-md text-sm text-muted-foreground hover:text-foreground transition-colors">Geri Al</button>
                        <button data-testid={`save-items-${o.order_number}`} onClick={() => saveItems(o)} disabled={savingItems === o.id} className="flex items-center gap-2 px-4 h-9 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors text-sm disabled:opacity-60">
                          {savingItems === o.id ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Değişiklikleri Kaydet
                        </button>
                      </div>
                    )}

                    <div className="bg-surface border border-border rounded-md p-3" data-testid={`gram-entry-${o.order_number}`}>
                      <div className="text-sm font-medium text-foreground flex items-center gap-2 mb-1">
                        <Scale size={14} className="text-gold" /> Gerçek Gram Girişi (İstanbul'dan gelince)
                      </div>
                      <p className="text-[11px] text-muted-foreground mb-3">Her parçanın gerçek gramını girin. Tümü girilince sipariş kesinleşir ve borç müşteri bakiyesine işlenir.</p>
                      <div className="space-y-3">
                        {o.items.map((it) => {
                          const arr = (gramEdits[o.id] || {})[it.product_id] || [];
                          const sum = arr.reduce((s, x) => s + (parseFloat(x) || 0), 0);
                          return (
                            <div key={it.product_id} data-testid={`gram-row-${it.code}`} className="border-b border-border/40 pb-2">
                              <div className="text-[11px] text-muted-foreground font-mono mb-1">{it.code} · {it.name} · {it.quantity} adet{it.size ? ` · Boy ${it.size}` : ""} · İşç {it.milyem || 0} milyem</div>
                              <div className="flex flex-wrap items-center gap-2">
                                {arr.map((g, idx) => (
                                  <input key={idx} data-testid={`gram-input-${it.code}-${idx}`} type="number" step="0.01" value={g}
                                    onChange={(e) => setGramVal(o.id, it.product_id, idx, e.target.value)}
                                    placeholder={`#${idx + 1} gr`}
                                    className="w-20 h-8 rounded bg-obsidian border border-border text-foreground text-sm px-2 focus:outline-none focus:ring-1 focus:ring-gold" />
                                ))}
                                <span className="text-xs text-gold self-center">= {fmtGram(sum)} gr</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-end mt-3">
                        <button data-testid={`save-grams-${o.order_number}`} onClick={() => saveGrams(o)} disabled={savingGram === o.id}
                          className="flex items-center gap-2 px-4 h-9 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors text-sm disabled:opacity-60">
                          {savingGram === o.id ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Gramları Kaydet & Bakiyeye İşle
                        </button>
                      </div>
                    </div>

                    {o.note && (
                      <div className="bg-surface border border-border rounded-md p-3">
                        <div className="text-[11px] text-muted-foreground mb-1">Müşteri Notu</div>
                        <div className="text-sm text-foreground">{o.note}</div>
                      </div>
                    )}

                    <GramSummary breakdown={o.karat_breakdown} totalPureHas={o.total_pure_has} totalIscHas={o.total_isc_has} totalHas={o.total_has} />
                    <InvoicePanel order={o} onUpdated={updateOrder} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
